// ========================================
// Hook לתדריך הבוקר - ניתוח שינויים במלאי
// ========================================
import { useMemo } from 'react';
import { calculateNetStock, calculateDOI, getTrafficLight } from '../utils/calculations';

import { ALERT_TYPES, ALERT_CONFIG } from '../utils/briefingConstants';

/**
 * השוואת שני מערכי מלאי ויצירת רשימת התראות
 * @param {Array} currentData - נתוני היום
 * @param {Array} previousData - נתוני אתמול
 * @param {number} forecastFactor - מקדם תחזית
 * @param {Set|Array} excludedSkus - מק"טים מוחרגים (לא יכללו בתדריך)
 * @returns {Array} רשימת התראות
 */
export const compareSnapshots = (currentData, previousData, forecastFactor = 0, excludedSkus = new Set()) => {
    if (!currentData || !previousData) return [];

    const alerts = [];

    // המרה ל-Set אם הגיע כמערך
    const excludedSet = excludedSkus instanceof Set ? excludedSkus : new Set(excludedSkus);

    // יצירת מפה לחיפוש מהיר
    const previousMap = new Map(
        previousData.map(item => [item.SKU, item])
    );

    currentData.forEach(currentItem => {
        // דילוג על פריטים מוחרגים
        if (excludedSet.has(currentItem.SKU)) {
            return;
        }

        const prevItem = previousMap.get(currentItem.SKU);

        if (!prevItem) {
            // פריט חדש לגמרי - לא מתייחסים (יכול להיות טעינה ראשונה)
            return;
        }

        const currentNet = calculateNetStock(currentItem);
        const prevNet = calculateNetStock(prevItem);
        const currentDOI = calculateDOI(currentNet, currentItem.AvgDailySales, forecastFactor);

        // בדיקת חזרה למלאי
        if (prevNet <= 0 && currentNet > 0) {
            alerts.push({
                type: ALERT_TYPES.RESTOCK,
                sku: currentItem.SKU,
                description: currentItem.Description,
                prevValue: prevNet,
                currentValue: currentNet,
                priority: 1, // עדיפות גבוהה
            });
        }

        // בדיקת אזילה
        if (prevNet > 0 && currentNet <= 0) {
            alerts.push({
                type: ALERT_TYPES.STOCKOUT,
                sku: currentItem.SKU,
                description: currentItem.Description,
                prevValue: prevNet,
                currentValue: currentNet,
                priority: 0, // עדיפות קריטית
            });
        }

        // בדיקת שינוי דירוג
        const prevClass = prevItem.Classification || 'C';
        const currentClass = currentItem.Classification || 'C';

        if (prevClass !== currentClass) {
            const classOrder = { 'A': 1, 'B': 2, 'C': 3 };
            const isUpgrade = classOrder[currentClass] < classOrder[prevClass];

            alerts.push({
                type: isUpgrade ? ALERT_TYPES.TRENDING_UP : ALERT_TYPES.TRENDING_DOWN,
                sku: currentItem.SKU,
                description: currentItem.Description,
                prevValue: prevClass,
                currentValue: currentClass,
                priority: isUpgrade ? 2 : 4,
            });
        }

        // בדיקת מלאי נמוך חדש (DOI < 14 ימים)
        const prevDOI = calculateDOI(prevNet, prevItem.AvgDailySales, forecastFactor);
        if (prevDOI >= 14 && currentDOI < 14 && currentNet > 0) {
            alerts.push({
                type: ALERT_TYPES.LOW_STOCK,
                sku: currentItem.SKU,
                description: currentItem.Description,
                prevValue: prevDOI,
                currentValue: currentDOI,
                priority: 3,
            });
        }
    });

    // מיון לפי עדיפות
    return alerts.sort((a, b) => a.priority - b.priority);
};

/**
 * Hook לקבלת תדריך הבוקר
 * @param {Array} currentData - נתוני היום
 * @param {Array} previousData - נתוני אתמול (מ-localStorage או DB)
 * @param {number} forecastFactor - מקדם תחזית
 * @param {Set|Array} excludedSkus - מק"טים מוחרגים
 */
export const useMorningBriefing = (currentData, previousData, forecastFactor = 0, excludedSkus = new Set()) => {
    const alerts = useMemo(() => {
        return compareSnapshots(currentData, previousData, forecastFactor, excludedSkus);
    }, [currentData, previousData, forecastFactor, excludedSkus]);

    // סיכום לפי סוג
    const summary = useMemo(() => {
        const counts = {
            restock: 0,
            stockout: 0,
            trendingUp: 0,
            trendingDown: 0,
            lowStock: 0,
        };

        alerts.forEach(alert => {
            counts[alert.type]++;
        });

        return counts;
    }, [alerts]);

    return {
        alerts,
        summary,
        hasAlerts: alerts.length > 0,
        criticalCount: summary.stockout,
        positiveCount: summary.restock + summary.trendingUp,
    };
};
